// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Date/time display formatting.
 *
 * **Dates are deliberately NOT tied to the app's i18n language.** English is one
 * language but several date conventions — a peer in London writes 15/01/2026 and a
 * peer in New York writes 01/15/2026 — so `i18n.language` ("en") can never answer
 * "which order?". Instead the order comes from the viewer's own machine (the host
 * locale, i.e. macOS System Settings → Language & Region), with an explicit opt-out
 * in profile → Appearance for anyone whose OS setting doesn't match their preference.
 *
 * Consequences worth knowing:
 * - Switching the UI language does not change date order. That's intentional.
 * - Never pass a locale in from a call site. The preference is the only input, so
 *   every date in the app renders identically for a given viewer.
 *
 * This module is kept free of localStorage/React so it stays unit-testable; the
 * preference is read and made reactive in `@/lib/preferences`.
 */

/** How the viewer wants dates ordered. `system` follows the host locale. */
export const DATE_FORMAT_OPTIONS = ["system", "dmy", "mdy", "iso"] as const;

export type DateFormatPreference = (typeof DATE_FORMAT_OPTIONS)[number];

export const DEFAULT_DATE_FORMAT: DateFormatPreference = "system";

/**
 * Concrete locale used to realise each preference. These are carriers for a date
 * *pattern*, not a language — only the numeric field order and separators are taken
 * from them, and month/weekday names are never rendered from these in numeric mode.
 * `sv-SE` is the standard trick for ISO-8601-shaped output (2026-01-15 09:30).
 */
const PREFERENCE_LOCALES: Record<Exclude<DateFormatPreference, "system">, string> = {
  dmy: "en-GB",
  mdy: "en-US",
  iso: "sv-SE",
};

/**
 * Reference date used to preview each option in the profile picker. Built in *local* time
 * (not UTC) so the preview never shows the 14th to a viewer west of Greenwich — the 15th
 * is the point, since it's unambiguously a day-of-month.
 */
export const DATE_FORMAT_SAMPLE_DATE = new Date(2026, 0, 15, 12, 0);

/**
 * Resolve a preference to a locale tag for `Intl`. `undefined` means "let the runtime
 * pick", which is what makes `system` follow the host's regional settings.
 */
export function resolveDateLocale(preference: DateFormatPreference = DEFAULT_DATE_FORMAT): string | undefined {
  return preference === "system" ? undefined : PREFERENCE_LOCALES[preference];
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
// 24h everywhere, independent of locale and of the date preference: these render into
// dense table columns where AM/PM costs width and breaks digit alignment.
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = { ...DATE_OPTIONS, ...TIME_OPTIONS };
const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };
const MONTH_DAY_OPTIONS: Intl.DateTimeFormatOptions = { month: "2-digit", day: "2-digit" };

/** `YYYY-MM-DD` — a calendar date, carrying no time and therefore no timezone. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  // A bare calendar date is not an instant, so it cannot be translated between zones: a store's
  // go-live date is the same day in Wrocław and in New York. `new Date("2026-01-15")` reads it as
  // UTC midnight, which then renders as the 14th anywhere west of UTC. Build it from local parts
  // instead, so the day shown is the day given.
  const parts = DATE_ONLY.exec(value);
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return new Date(value);
}

export function formatDate(value: string | Date | undefined | null, preference?: DateFormatPreference): string {
  if (!value) return "—";
  return toDate(value).toLocaleDateString(resolveDateLocale(preference), DATE_OPTIONS);
}

export function formatTime(value: string | Date | undefined | null, preference?: DateFormatPreference): string {
  if (!value) return "—";
  return toDate(value).toLocaleTimeString(resolveDateLocale(preference), TIME_OPTIONS);
}

export function formatDateTime(value: string | Date | undefined | null, preference?: DateFormatPreference): string {
  if (!value) return "—";
  return toDate(value).toLocaleString(resolveDateLocale(preference), DATE_TIME_OPTIONS);
}

/**
 * Spelled-out month form (e.g. "15 January 2026") for grouping headers, where a numeric
 * date reads poorly. Month names come from the resolved locale; under `iso` there is no
 * meaningful long form, so it falls back to the numeric ISO shape.
 */
export function formatDateLong(value: string | Date | undefined | null, preference?: DateFormatPreference): string {
  if (!value) return "—";
  if (preference === "iso") return formatDate(value, preference);
  return toDate(value).toLocaleDateString(resolveDateLocale(preference), LONG_DATE_OPTIONS);
}

const PROSE_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
};

/**
 * A date written out as words in the UI language, e.g. "Tuesday, 6 October 2026" / "wtorek, 6
 * października 2026" — for prose such as the Home greeting, never for data.
 *
 * The one place a date follows `i18n.language` rather than the date preference: the weekday and
 * month are words in a sentence, and the sentence around them is already in that language. Like
 * the weekday names in `timezone-cell.tsx`, it's words, not a day/month order a reader could
 * misread. Anything tabular or numeric keeps going through `formatDate` and friends.
 */
export function formatDateProse(value: string | Date | undefined | null, language: string): string {
  if (!value) return "—";
  return toDate(value).toLocaleDateString(language, PROSE_DATE_OPTIONS);
}

/** A recurring `MM/DD` key, e.g. an annual opening-hours override. */
const MONTH_DAY = /^(\d{2})\/(\d{2})$/;

/**
 * A yearless calendar day (`MM/DD` in), in the viewer's day/month order — the raw key is ambiguous
 * to read, since `12/25` is Christmas to an American and nothing at all to a European.
 * A leap year backs the parse so `02/29` survives it.
 */
export function formatMonthDay(value: string | undefined | null, preference?: DateFormatPreference): string {
  const parts = value ? MONTH_DAY.exec(value) : null;
  if (!parts) return "—";
  // sv-SE (the `iso` stand-in) has no yearless ISO form — it renders 25/12 — so spell it out here.
  if (preference === "iso") return `${parts[1]}-${parts[2]}`;
  const date = new Date(2024, Number(parts[1]) - 1, Number(parts[2]));
  return date.toLocaleDateString(resolveDateLocale(preference), MONTH_DAY_OPTIONS);
}

/**
 * Machine-readable UTC timestamp for CSV export. Deliberately ignores the display
 * preference: a localised date in a spreadsheet cell is re-interpreted by whatever
 * regional settings the recipient's Excel happens to have, silently swapping day and
 * month. ISO 8601 is unambiguous to every spreadsheet and sorts lexicographically.
 */
export function formatIsoDateTime(value: string | Date | undefined | null): string {
  if (!value) return "";
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/** Format seconds as `Xs`, `Ym Zs`, or `Hh Mm Ss`. */
export function formatDuration(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null) return "—";
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}
